/**
 * Customer-facing wording about the AI assistant, in one place.
 *
 * Which model provider answers is a deployment decision made on the server
 * (web/api/ai/_provider.js): Anthropic, Azure OpenAI Service, or OpenAI. A
 * customer never needs to know which, and must never be told to set up a
 * particular vendor or an environment variable. Every "AI is off" notice in
 * the app reads from here so the wording cannot drift back to naming one.
 *
 * PURE: strings and string helpers only, no imports, safe in any test.
 */

/** The AI endpoint is not enabled on this build. */
export const AI_NOT_ENABLED = 'The AI assistant is not turned on for this workspace. Ask your administrator to enable it.'

/**
 * The AI endpoint is enabled but the server's provider configuration is
 * incomplete. The server's own message names the missing settings, which is
 * for an administrator (the AI Connection page lists them in plain words), not
 * for whoever pressed the button.
 */
export const AI_NOT_CONFIGURED =
  'The AI assistant is not fully set up on this deployment. Ask your administrator to finish the AI configuration.'

/** How the assistant is named wherever a vendor name used to appear. */
export const AI_ASSISTANT_LABEL = 'AI assistant'

/** Shown or stored where a model name is expected and the server reported none. */
export const AI_MODEL_LABEL = 'AI model'

/** The deployment's provider has no hosted web search. */
export const LIVE_WEB_RESEARCH_UNAVAILABLE =
  'Live web research is not available on this deployment. This brief was written from internal data only.'

/** The same fact as a standing note, before any brief has been written. */
export const LIVE_WEB_RESEARCH_UNAVAILABLE_NOTE =
  'Live web research is not available on this deployment. Briefs are written from internal data only.'

/**
 * "AI is off" plus what still works without it, for a feature that has a
 * manual path ("You can still build the rule by hand.").
 */
export function aiNotEnabled(stillWorks?: string): string {
  const extra = (stillWorks ?? '').trim()
  return extra ? `${AI_NOT_ENABLED} ${extra}` : AI_NOT_ENABLED
}

/**
 * The model to show or store as the record of what produced an output: the
 * name the server reported on the response when there is one, else a neutral
 * label. The model id a caller REQUESTED is only a tier hint to the server and
 * is never a true record of what answered.
 */
export function reportedModelLabel(reported: unknown): string {
  const name = typeof reported === 'string' ? reported.trim() : ''
  return name || AI_MODEL_LABEL
}

/** The reported model, or null when the server named none (for nullable columns). */
export function reportedModelOrNull(reported: unknown): string | null {
  const name = typeof reported === 'string' ? reported.trim() : ''
  return name || null
}

/** Error types the server words for a customer; their message is shown as is. */
const CUSTOMER_WORDED_ERROR_TYPES = new Set(['unsupported_document'])

interface AiErrorBody {
  type?: unknown
  error?: { type?: unknown; message?: unknown } | null
}

/** How the server's "provider configuration is incomplete" message starts (web/api/ai/_model.js). */
const NOT_CONFIGURED_PREFIX = 'AI is not configured on this deployment'

/**
 * A message safe to show the person at the screen for an AI error body, or
 * null when the body is anything else and the caller should use its usual
 * wording. Two cases:
 *
 *   - an error the server has already worded for a customer (today: a
 *     document this deployment's model cannot read) is returned as is;
 *   - the server's "not configured" error, which lists setting names, becomes
 *     the plain "ask your administrator" line.
 *
 * Accepts the raw response text or an already-parsed body. Never throws.
 */
export function customerWordedAiError(body: unknown): string | null {
  let parsed: AiErrorBody | null = null
  if (typeof body === 'string') {
    const text = body.trim()
    if (!text.startsWith('{')) return null
    try {
      parsed = JSON.parse(text) as AiErrorBody
    } catch {
      return null
    }
  } else if (body && typeof body === 'object') {
    parsed = body as AiErrorBody
  }
  const err = parsed?.error
  if (!err || typeof err !== 'object') return null
  const message = typeof err.message === 'string' ? err.message.trim() : ''
  if (message.startsWith(NOT_CONFIGURED_PREFIX)) return AI_NOT_CONFIGURED
  if (typeof err.type !== 'string' || !CUSTOMER_WORDED_ERROR_TYPES.has(err.type)) return null
  return message || null
}
